import { apiClient, ApiResult } from "@/lib/api-client"
import type { Organization } from "../entities/Organization"
import { OrganizationStatus } from "@/modules/organizations/enums/organization-status"
import { PlanType } from "../types/plan-type"

// Backend DTO interfaces
interface OrganizationDto {
  id: string
  name: string
  physicalAddress?: {
    line1?: string
    line2?: string
    city?: string
    state?: string
    country?: string
    postalCode?: string
    complexNumber?: string
    complexName?: string
  }
  currency: string
  plan: PlanType
  status: OrganizationStatus
  createdAt: string
  updatedAt: string
  isDeleted?: boolean
  deletedAt?: string | null
  userRole?: string
  description?: string
  welcomeMessage?: string
  businessPhone?: string
}

function mapOrganizationDto(dto: OrganizationDto): Organization {
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description,
    businessPhone: dto.businessPhone,
    welcomeMessage: dto.welcomeMessage,
    plan: dto.plan,
    physicalAddress: dto.physicalAddress
      ? {
          line1: dto.physicalAddress.line1 || "",
          line2: dto.physicalAddress.line2 || undefined,
          city: dto.physicalAddress.city || "",
          state: dto.physicalAddress.state || "",
          country: dto.physicalAddress.country || "",
          postalCode: dto.physicalAddress.postalCode || "",
          complexNumber: dto.physicalAddress.complexNumber || undefined,
          complexName: dto.physicalAddress.complexName || undefined,
        }
      : {
          line1: "",
          city: "",
          state: "",
          country: "",
          postalCode: "",
        },
    currency: dto.currency,
    status: dto.status,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    isDeleted: dto.isDeleted,
    deletedAt: dto.deletedAt ?? null,
    userRole: dto.userRole,
  }
}

export type CreateOrganizationDto = {
  name: string
  plan: PlanType
  physicalAddress: Organization["physicalAddress"]
  couponCode?: string
}

export type UpdateOrganizationDto = Partial<CreateOrganizationDto> & {
  status?: OrganizationStatus
  isDeleted?: boolean
  description?: string
  welcomeMessage?: string
  businessPhone?: string
}

export async function getOrganizations(): Promise<Organization[]> {
  const res = await apiClient.get<OrganizationDto[]>("/organization")
  return res.data.map(mapOrganizationDto)
}

export async function getOrganizationById(id: string): Promise<Organization> {
  const res = await apiClient.get<OrganizationDto>(`/organizations/${id}`)
  return mapOrganizationDto(res.data)
}

export async function createOrganization(
  dto: CreateOrganizationDto,
): Promise<ApiResult<Organization>> {
  const res = await apiClient.post<ApiResult<Organization>>("/organization", dto)
  return res.data
}

export async function updateOrganization(
  id: string,
  dto: UpdateOrganizationDto,
): Promise<Organization> {
  const res = await apiClient.put<Organization>(`/organizations/${id}`, dto)
  return res.data
}

export async function deleteOrganization(id: string): Promise<void> {
  await apiClient.delete(`/organizations/${id}`)
}

export interface InviteInfo {
  organizationName: string
  status: number
  isNewUser: boolean
  email: string
}

export async function getInviteInfo(token: string): Promise<InviteInfo> {
  const res = await apiClient.get<ApiResult<InviteInfo>>(`/organizations/invites/${token}`)
  if (!res.data.isSuccess || !res.data.value) throw new Error(res.data.error || "Invite not found")
  return res.data.value
}

export interface AcceptOrganizationInvitePayload {
  email: string
  tempPassword?: string
  newPassword?: string
}

export async function acceptInvite(token: string, payload: AcceptOrganizationInvitePayload): Promise<void> {
  const res = await apiClient.post<ApiResult<void>>(`/organizations/invites/${token}/accept`, payload)
  if (!res.data.isSuccess) throw new Error(res.data.error || "Failed to accept invite")
}

export async function rejectInvite(token: string): Promise<void> {
  const res = await apiClient.post<ApiResult<void>>(`/organizations/invites/${token}/reject`)
  if (!res.data.isSuccess) throw new Error(res.data.error || "Failed to reject invite")
}
